import React from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  LayoutDashboard, Users as UsersIcon, Settings, ScrollText, LogOut,
  Users2, FileText, CalendarDays, Palette, Wallet, Pencil, Archive,
  LayoutGrid, Contact,
  Database, Tag, Ruler, Shirt, Clock, Gift, ShieldCheck, Factory,
  Warehouse, Package, ClipboardList, Boxes, ArrowLeftRight,
  History, SlidersHorizontal, ClipboardCheck, ShoppingCart, Truck,
  PackageSearch, PackageCheck, Undo2, MessageSquareWarning, ReceiptText, Banknote, HandCoins, BellRing,
  Scissors, Gauge, Wrench, CalendarClock, Siren,
  Sun, Moon, PackagePlus, BookOpen, CircleHelp, Landmark,
} from "lucide-react";
import {
  PURCHASING_ROLES,
  VENDOR_FINANCE_ROLES,
  INVENTORY_SECTION_ROLES,
  INVENTORY_MATERIALS_ROLES,
  INVENTORY_LEDGER_ROLES,
  INVENTORY_ADJUSTMENT_ROLES,
  INVENTORY_OPNAME_ROLES,
  INVENTORY_MATERIAL_ISSUE_ROLES,
  DESIGN_TASK_NAV_ROLES,
  SPV_REVIEW_ROLES,
  SPECIAL_OPS_ROLES,
  RECEIVABLE_NAV_ROLES,
  REFUND_NAV_ROLES,
  COMPLAINT_NAV_ROLES,
  PDF_ARCHIVE_ROLES,
  PAPAN_KERJA_ROLES,
  JAM_MASUK_NAV_ROLES,
  WO_USUL_ROLES,
  PERCAKAPAN_ROLES,
  KANAL_WA_ROLES,
  BORONGAN_TULIS_ROLES,
  SERAH_TERIMA_CUSTOMER_ROLES,
  TINDAK_LANJUT_PESANAN_ROLES,
  BORONGAN_BACA_ROLES,
  ABSENSI_REKAP_ROLES,
  TRACKING_ROLES,
  FA_LAMA_ROLES,
  LAPORAN_ROLES,
  PAPAN_KONTROL_ROLES,
  PAPAN_KONTROL_AMBANG_ROLES,
  PAPAN_TV_ROLES,
  ASET_NAV_ROLES,
  CUSTOMER_ROLES,
  DR_READ_ROLES,
  WO_READ_ROLES,
  ALARM_BAHAN_ROLES,
  BARANG_BERKURANG_ROLES,
  PINDAH_BAHAN_ROLES,
  LAPORAN_BALIK_ROLES,
  PAYROLL_ROLES,
  UANG_MASUK_ROLES,
  ANALITIK_ROLES,
  KPI_ROLES,
  KARYAWAN_ROLES,
  FA_LIST_ROLES,
} from "../lib/roles";
// Bertindak Atas Nama — fitur PERMANEN (K140), bukan dev tool; saklar ETAMS_DELEGASI.
import DevRoleSwitcher from "./dev/DevRoleSwitcher";
import { HanyaBacaContext } from "../lib/hanyaBaca";
import TanyaEtams from "./etams/TanyaEtams";
import Breadcrumb, { JudulHalamanProvider } from "./etams/Breadcrumb";
// Sidebar Versi A/B + Cari menu + panah atas — keputusan tampilan Owner 24 Sep 2026 (VONIS-24SEP-SORE 7–10).
import {
  NavProvider, NavItem, KepalaKelompok, NavAtas, CariMenu, TombolCiut, RailCiut, TombolKeAtas, useNav,
} from "./nav/NavKit";

// Label mode sistem untuk staf (K200 butir 6) — kode mentah app_mode tidak tampil.
const LABEL_MODE = {
  UAT: { label: "Uji coba", title: "Data di sistem ini masih data uji coba." },
  PRODUCTION: { label: "Operasional", title: "Sistem berjalan dengan data asli." },
  DIBEKUKAN: { label: "Dibekukan", title: "Input baru dihentikan sementara oleh Owner." },
};

export default function Layout() {
  const { profile, appMode, signOut, isOwner, isKepalaCabang, isCS, roleCode, impersonation, isPemantau } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const mainRef = React.useRef(null);

  // ⚠ `pathname` saja, SENGAJA bukan seluruh objek lokasi. Perubahan query
  //   atau hash terjadi saat orang menyaring/membuka bagian DI HALAMAN YANG
  //   SAMA — menggulir ke atas di situ justru membuang tempat yang sedang ia
  //   baca, dan itu kebalikan dari maksud permintaannya.
  React.useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [pathname]);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  const mode = appMode?.mode || "UAT";

  return (
    <HanyaBacaContext.Provider value={isPemantau === true}>
    <NavProvider userId={profile?.id}>
    <div className="min-h-screen flex flex-col md:flex-row bg-background">
      <SisiNav>
        <nav className="nav-penuh p-2 flex md:flex-col gap-0.5 flex-1 overflow-auto">
          <NavAtas />
          {/* ⛔ SIDEBAR DIKELOMPOKKAN GAYA ERP, 31 Agustus 2026 (permintaan Owner).
              Sebelumnya 48 butir dengan HANYA DUA kelompok (Inventori,
              Purchasing) — sisanya menumpuk sebagai satu daftar panjang, dan
              satu kantong bernama "Special Ops" menampung sembilan butir yang
              nol punya hubungan satu sama lain: Piutang, Refund, Komplain,
              Arsip PDF, Papan Kerja, Personil, Jam Masuk.

              Pengelompokan ini mengikuti pembagian modul ERP mapan — alur
              uang masuk (Penjualan), alur uang keluar (Purchasing), barang
              (Inventori), pengerjaan (Desain, Produksi), orang (SDM), lalu
              rujukan (Master Data) dan pengawasan (Dokumen & Audit).

              ⚠ NOL SATU PUN GERBANG PERAN DIUBAH. Yang berpindah cuma
              tempatnya di layar; setiap butir membawa gerbangnya sendiri
              persis seperti sebelumnya. Penjaga sebenarnya tetap 403 backend
              — menyembunyikan menu bukan keamanan. */}
          {/* ⛔ URUTAN DI DALAM TIAP KELOMPOK — konvensi ERP mapan, 2 Sep 2026
              (K143, permintaan Owner: "bandingkan dengan Odoo ... agar flow
              penggunaannya tertata"). Setiap kelompok disusun dengan satu
              aturan yang sama:

                 RINGKASAN  ->  OPERASIONAL  ->  LAPORAN  ->  PENGATURAN

              Ringkasan   : papan/posisi yang dibuka PERTAMA tiap pagi
              Operasional : transaksi harian, diurut mengikuti ALUR kerjanya
              Laporan     : rekap, mutasi, riwayat — dibaca, bukan ditulis
              Pengaturan  : master data & konfigurasi — dipakai bulanan,
                            SELALU paling bawah

              Kenapa satu aturan untuk semua: 58 butir menu terasa seperti
              satu produk hanya kalau mata belajar SEKALI di mana "master"
              berada, lalu benar di 11 kelompok. Sebelum ini Vendor duduk di
              PUNCAK Purchasing dan Master Bahan di PUNCAK Inventori — master
              data mendahului pekerjaan hariannya sendiri.

              ⚠ Keputusan terkunci yang lebih tua MENANG atas konvensi ini.
              Tracking Pesanan tetap tepat sesudah Customer (K137, alasan
              Owner: CS membukanya sambil memegang nama customer di telepon)
              walau menurut konvensi ia "laporan". */}
          <NavItem to="/dashboard" icon={LayoutDashboard} testId="nav-dashboard">Dashboard</NavItem>
          {/* ⛔ Dashboard Umum duduk di sini, bukan di Produksi — permintaan
              Owner 2 Sep 2026. Ia BUKAN layar produksi; ia ringkasan yang
              dibaca seisi ruangan dari TV. Menaruhnya di Produksi membuat
              orang mencarinya di tempat ia bukan miliknya.

              ⚠ Rutenya SENGAJA di luar <Layout/> (lihat App.js): dibuka dari
              sini ia mengambil layar penuh tanpa menu — memang begitu
              maksudnya di TV. */}
          {PAPAN_TV_ROLES.includes(roleCode) && (
            <NavItem to="/dashboard-umum" icon={Gauge} testId="nav-papan-tv">Dashboard Umum</NavItem>
          )}

          <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-penjualan-section">
            <KepalaKelompok id="penjualan" icon={Users2} judul="Penjualan" />
          {/* Urutan K143 — alur uang masuk: Customer -> (Tracking, K137) ->
              Order (FA) -> Permintaan Desain -> Konfirmasi Pembayaran ->
              Percakapan (riwayat).

              ⚠ Order (FA) sebelumnya PALING BAWAH — transaksi inti modul
              penjualan duduk sesudah riwayat percakapannya sendiri. */}
          {/* K190 butir 7 (22 Sep 2026): menu mengikuti route — route kini
              menyebut peran (cermin backend), jadi menu yang berujung
              "ditolak" tidak boleh tampil (K89). */}
          {CUSTOMER_ROLES.includes(roleCode) && (
            <NavItem to="/customers" icon={Users2} testId="nav-customers">Customer</NavItem>
          )}
          {/* K137 — Owner menyebut kelompoknya sendiri: "di kelompok
              penjualan". Ia duduk tepat sesudah Customer karena CS membukanya
              sambil memegang nama customer di telepon. K137 MENANG atas
              konvensi K143 (yang akan menaruhnya di tier Laporan). */}
          {TRACKING_ROLES.includes(roleCode) && (
            <NavItem to="/tracking" icon={PackageSearch} testId="nav-tracking">Tracking Pesanan</NavItem>
          )}
          {/* K206 / VONIS-HAK-BACA-ISI-FA: Finance dan Admin Purchasing tidak membaca daftar FA (backend 403). */}
          {FA_LIST_ROLES.includes(roleCode) && (
            <NavItem to="/fa-orders" icon={FileText} testId="nav-fa">Order (FA)</NavItem>
          )}
          {/* FA Lama duduk tepat di bawah Order (FA): ia FA juga — yang lahir
              sebelum sistem. Hidup hanya selama jendela 48 jam (Q39/Q45);
              gerbangnya di backend, menu ini cuma pintu. */}
          {FA_LAMA_ROLES.includes(roleCode) && (
            <NavItem to="/fa-lama" icon={History} testId="nav-fa-lama">FA Lama (cutover)</NavItem>
          )}
          {DR_READ_ROLES.includes(roleCode) && (
            <NavItem to="/design-requests" icon={Palette} testId="nav-dr">Permintaan Desain</NavItem>
          )}
          {/* K89 + K55 — kedua butir desain PINDAH ke kelompok Desain di bawah.
              Gerbangnya ikut apa adanya: DESIGN_TASK_NAV_ROLES dan
              SPV_REVIEW_ROLES, nol satu pun dilonggarkan.

              ⚠ Owner ikut melihat Review Desain karena ia boleh MEMBACA
              antrean. Ia nol bisa memvonis, dan itu ditegakkan CHECK tabelnya
              — bukan oleh disembunyikannya menu ini. */}
          {(isOwner || isKepalaCabang) && (
            <NavItem to="/payments/pending" icon={Wallet} testId="nav-payments-pending">Konfirmasi Pembayaran</NavItem>
          )}
          {/* BUSINESS-50/51/52 (26 Sep 2026): FA menunggu DP, customer penunggak, barang jadi belum diambil. */}
          {TINDAK_LANJUT_PESANAN_ROLES.includes(roleCode) && (
            <NavItem to="/tindak-lanjut-pesanan" icon={ClipboardList} testId="nav-tindak-lanjut-pesanan">Tindak Lanjut Pesanan</NavItem>
          )}
          {/* Percakapan duduk di Penjualan, bukan modul sendiri: ia RIWAYAT
              HUBUNGAN dengan customer, dan yang membukanya sedang mengurus
              pesanan orang itu — bukan sedang mengurus WhatsApp. Sebagai
              riwayat ia tier Laporan: paling bawah di kelompok ini. */}
          {PERCAKAPAN_ROLES.includes(roleCode) && (
            <NavItem to="/percakapan" icon={MessageSquareWarning} testId="nav-percakapan">Percakapan</NavItem>
          )}
          {/* K207 W6: keadaan nomor WA cabang + pesan yang tertahan saat layanan gangguan (F1/F3). */}
          {KANAL_WA_ROLES.includes(roleCode) && (
            <NavItem to="/kanal-wa" icon={Siren} testId="nav-kanal-wa">Kanal WhatsApp</NavItem>
          )}
          </div>

          {/* ⛔ Kelompok digerbangi oleh GABUNGAN gerbang isinya — ditemukan
              UAT per jabatan 2 Sep 2026: Admin Gudang, Finance, dan Admin
              Purchasing melihat judul "DESAIN" KOSONG karena kedua butirnya
              tersembunyi tapi judulnya tidak. Pola yang sama sudah dipakai
              Layanan Khusus di bawah. Nol gerbang butir yang berubah. */}
          {(DESIGN_TASK_NAV_ROLES.includes(roleCode) || SPV_REVIEW_ROLES.includes(roleCode)) && (
          <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-desain-section">
            <KepalaKelompok id="desain" icon={Palette} judul="Desain" />
            {DESIGN_TASK_NAV_ROLES.includes(roleCode) && (
              <NavItem to="/design-tasks" icon={Pencil} testId="nav-design-tasks">Tugas Desainer</NavItem>
            )}
            {SPV_REVIEW_ROLES.includes(roleCode) && (
              <NavItem to="/spv-review" icon={ShieldCheck} testId="nav-spv-review">Review Desain</NavItem>
            )}
          </div>
          )}

          {/* Seksi tampil hanya bila ada butir yang boleh dilihat peran ini —
              sejak K190 butir 7 semua butir di dalamnya bergerbang, jadi tanpa
              ini Finance/Purchasing melihat judul "Produksi" yang kosong. */}
          {[PAPAN_KERJA_ROLES, WO_USUL_ROLES, WO_READ_ROLES, BORONGAN_TULIS_ROLES].some((r) => r.includes(roleCode)) && (
          <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-produksi-section">
            <KepalaKelompok id="produksi" icon={Factory} judul="Produksi" />
            {/* Urutan K143 — Papan Kerja (RINGKASAN: yang dibuka pertama
                tiap pagi) -> Usulan Draf WO -> Work Order -> Serah Terima
                Tahap (alur kerja: usulan lahir SEBELUM WO, serah terima
                SESUDAH WO berjalan). */}
            {PAPAN_KERJA_ROLES.includes(roleCode) && (
              <NavItem to="/papan-kerja" icon={LayoutGrid} testId="nav-papan-kerja">Papan Kerja</NavItem>
            )}
            {/* Butir H — usulan auto-draft. Tepat di ATAS WO karena ia pintu
                masuk ke WO, bukan modul terpisah — pintu masuk mendahului
                ruangannya. */}
            {WO_USUL_ROLES.includes(roleCode) && (
              <NavItem to="/wo/usul" icon={Gauge} testId="nav-wo-usul">Usulan Draf WO</NavItem>
            )}
            {WO_READ_ROLES.includes(roleCode) && (
              <NavItem to="/wo" icon={Factory} testId="nav-wo">Work Order</NavItem>
            )}
            {LAPORAN_BALIK_ROLES.includes(roleCode) && (
              <NavItem to="/produksi/laporan-balik" icon={ClipboardCheck} testId="nav-laporan-balik">Laporan Balik Telegram</NavItem>
            )}
            {/* ⛔ Serah terima duduk di PRODUKSI, bukan di modul upah: yang
                melakukannya adalah orang lantai produksi saat pekerjaan
                berpindah tahap. Upahnya AKIBAT, bukan alasannya. */}
            {BORONGAN_TULIS_ROLES.includes(roleCode) && (
              <NavItem to="/borongan/serah-terima" icon={ArrowLeftRight} testId="nav-serah-terima">Serah Terima Tahap</NavItem>
            )}
            {SERAH_TERIMA_CUSTOMER_ROLES.includes(roleCode) && (
              <NavItem to="/serah-terima" icon={ArrowLeftRight} testId="nav-serah-terima-customer">Serah Terima Customer</NavItem>
            )}
          </div>
          )}
          {INVENTORY_SECTION_ROLES.includes(roleCode) && (
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-inventory-section">
              <KepalaKelompok id="inventory" icon={Warehouse} judul="Inventori" />
              {/* Urutan K143:
                    RINGKASAN    Barang Jadi (posisi stok yang dilihat pertama)
                    OPERASIONAL  Pengeluaran Bahan -> Penyesuaian Stok ->
                                 Stock Opname -> Transfer Antar Cabang
                    LAPORAN      Mutasi Stok Bahan -> Mutasi Barang Jadi
                    PENGATURAN   Master Bahan

                  ⚠ Master Bahan sebelumnya di PUNCAK kelompok — master data
                  yang dipakai bulanan menempati baris pertama di atas
                  pekerjaan harian gudang. */}
              <NavItem to="/inventory/finished-goods" icon={Boxes} testId="nav-inv-fg">Barang Jadi</NavItem>
              {INVENTORY_MATERIAL_ISSUE_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/material-issues" icon={ClipboardList} testId="nav-inv-mi">Pengeluaran Bahan</NavItem>
              )}
              {INVENTORY_ADJUSTMENT_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/adjustments" icon={SlidersHorizontal} testId="nav-inv-adjust">Penyesuaian Stok</NavItem>
              )}
              {INVENTORY_OPNAME_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/opname" icon={ClipboardCheck} testId="nav-inv-opname">Stock Opname</NavItem>
              )}
              <NavItem to="/inventory/finished-goods/transfers" icon={ArrowLeftRight} testId="nav-inv-transfer">Transfer Antar Cabang</NavItem>
              {INVENTORY_LEDGER_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/stock/ledger" icon={History} testId="nav-inv-ledger">Mutasi Stok Bahan</NavItem>
              )}
              <NavItem to="/inventory/finished-goods/movements" icon={History} testId="nav-inv-fg-mov">Mutasi Barang Jadi</NavItem>
              {INVENTORY_MATERIALS_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/materials" icon={Package} testId="nav-inv-materials">Master Bahan</NavItem>
              )}
              {/* Vonis Owner 25 Sep 2026 sore butir 2: layar baru BOLEH menambah menu (69 menu lama tetap utuh).
                  Menu baru tercatat di backend/tests/snapshot/sidebar_menu.json → "menu_baru". */}
              {ALARM_BAHAN_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/alarm-bahan" icon={Siren} testId="nav-inv-alarm-bahan">Alarm Bahan</NavItem>
              )}
              {BARANG_BERKURANG_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/barang-berkurang" icon={ClipboardList} testId="nav-inv-barang-berkurang">Barang Berkurang</NavItem>
              )}
              {PINDAH_BAHAN_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/pindah-bahan" icon={ArrowLeftRight} testId="nav-inv-pindah-bahan">Pindah Bahan Baku</NavItem>
              )}
              {INVENTORY_ADJUSTMENT_ROLES.includes(roleCode) && (
                <NavItem to="/inventory/stok-awal" icon={PackagePlus} testId="nav-inv-stok-awal">Stok Awal Bahan</NavItem>
              )}
              <NavItem to="/inventory/telusur-lot" icon={History} testId="nav-inv-telusur-lot">Telusur Lot Kain</NavItem>
            </div>
          )}
          {/* L1/L2 (Owner, 16 Sep 2026) — kelompok "Aset" SESUDAH Inventori,
              untuk OWNER/KEPALA_CABANG/ADMIN_GUDANG/ADMIN_PRODUKSI (cermin
              `aset_meta.MENU`). Urutan penuh K143: Jadwal Maintenance →
              Laporan Bulanan Aset → Daftar Aset — lengkap sejak 4b-3
              (16 Sep 2026). */}
          {ASET_NAV_ROLES.includes(roleCode) && (
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-aset-section">
              <KepalaKelompok id="aset" icon={Wrench} judul="Aset" />
              <NavItem to="/aset/jadwal" icon={CalendarClock} testId="nav-aset-jadwal">Jadwal Maintenance</NavItem>
              <NavItem to="/aset/laporan-bulanan" icon={ClipboardCheck} testId="nav-aset-laporan">Laporan Bulanan Aset</NavItem>
              <NavItem to="/aset" icon={Wrench} testId="nav-aset-daftar">Daftar Aset</NavItem>
              <NavItem to="/aset/inventaris" icon={Package} testId="nav-aset-inventaris">Inventaris Operasional</NavItem>
            </div>
          )}
          {PURCHASING_ROLES.includes(roleCode) && (
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-purchasing-section">
              <KepalaKelompok id="purchasing" icon={ShoppingCart} judul="Purchasing" />
              {/* Urutan K143 — alur uang keluar persis seperti kejadiannya:
                    Permintaan Pembelian -> Purchase Order -> Penerimaan
                    Barang -> Retur Vendor -> [Uang Muka -> Tagihan ->
                    Pembayaran] -> Vendor (PENGATURAN, paling bawah).

                  ⚠ Vendor sebelumnya di PUNCAK: master data mendahului
                  pekerjaan yang memakainya. Dan Uang Muka duduk SESUDAH
                  Pembayaran, padahal DP dibayar sebelum tagihan pernah ada. */}
              <NavItem to="/purchasing/pr" icon={ClipboardList} testId="nav-pur-pr">Permintaan Pembelian</NavItem>
              <NavItem to="/purchasing/po" icon={PackageSearch} testId="nav-pur-po">Purchase Order</NavItem>
              <NavItem to="/purchasing/receiving" icon={PackageCheck} testId="nav-pur-grn">Penerimaan Barang</NavItem>
              <NavItem to="/purchasing/returns" icon={Undo2} testId="nav-pur-returns">Retur Vendor</NavItem>
              {/* Uang keluar ke vendor — 4 role saja. Himpunannya di
                  lib/roles.js, cermin INVOICE_READ_ROLES di
                  backend. CS/Gudang/Produksi sengaja tidak melihat menu ini
                  (Fase 3 F9): nilai tagihan dan status bayar ke vendor bukan
                  bagian pekerjaan mereka. */}
              {VENDOR_FINANCE_ROLES.includes(roleCode) && (
                <>
                  <NavItem to="/purchasing/dp" icon={HandCoins} testId="nav-pur-dp">
                    Uang Muka Vendor
                  </NavItem>
                  <NavItem to="/purchasing/invoices" icon={ReceiptText} testId="nav-pur-invoices">
                    Tagihan Vendor
                  </NavItem>
                  <NavItem to="/purchasing/payments" icon={Banknote} testId="nav-pur-payments">
                    Pembayaran Vendor
                  </NavItem>
                </>
              )}
              <NavItem to="/purchasing/vendors" icon={Truck} testId="nav-pur-vendors">Vendor</NavItem>
            </div>
          )}
          {/* Bagian tampil kalau role berhak atas SALAH SATU butir di dalamnya.
              Dulu digerbangi `(isOwner || isKepalaCabang || isCS)` untuk
              ketiganya sekaligus; sejak FINANCE dapat hak baca Piutang (K34),
              seksinya harus terbuka untuknya TANPA ikut membuka dua butir
              lain — K34 menolak `special_customers` dan `foc_requests`
              secara eksplisit. */}
          {/* ⚠ Gerbangnya DIPECAH PER KELOMPOK, bukan satu pembungkus untuk
              ketiganya. Satu ekspresi kondisional JSX cuma boleh mengembalikan
              SATU elemen; membungkus tiga kelompok dengan satu gerbang juga
              akan menampilkan judul kelompok yang seluruh isinya tersembunyi. */}
          {(SPECIAL_OPS_ROLES.includes(roleCode) || RECEIVABLE_NAV_ROLES.includes(roleCode)) && (
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-specialops-section">
              <KepalaKelompok id="specialops" icon={ShieldCheck} judul="Layanan Khusus" />
              {/* Urutan K143 — purna jual mengikuti sebab-akibatnya:
                    OPERASIONAL  Komplain (pemicu) -> Refund Customer (akibat)
                                 -> Permintaan Barang Gratis (FOC)
                    LAPORAN      Piutang (dipantau, bukan ditulis)
                    PENGATURAN   Customer Khusus (pemberian status — konfigurasi
                                 per customer, dipakai jarang) */}
              {COMPLAINT_NAV_ROLES.includes(roleCode) && (
                <NavItem to="/complaints" icon={MessageSquareWarning} testId="nav-complaints">Komplain</NavItem>
              )}
              {REFUND_NAV_ROLES.includes(roleCode) && (
                <NavItem to="/refunds" icon={Undo2} testId="nav-refunds">Refund Customer</NavItem>
              )}
              {/* Digerbangi EKSPLISIT sejak seksinya terbuka untuk FINANCE.
                  Sebelumnya butir ini menumpang gerbang seksi — bentuk yang
                  benar selama anggotanya sama, dan salah pada hari pertama
                  mereka berbeda. */}
              {SPECIAL_OPS_ROLES.includes(roleCode) && (
                <NavItem to="/foc-requests" icon={Gift} testId="nav-foc">Permintaan Barang Gratis (FOC)</NavItem>
              )}
              {RECEIVABLE_NAV_ROLES.includes(roleCode) && (
                <NavItem to="/receivables" icon={Wallet} testId="nav-receivables">Piutang</NavItem>
              )}
              {(isOwner || isKepalaCabang) && <NavItem to="/special-customers" icon={ShieldCheck} testId="nav-sc">Customer Khusus</NavItem>}
            </div>
          )}

          {/* SDM — orang yang mengerjakan, bukan pekerjaannya. Personil dan
              jam masuk dipakai peran yang SAMA (Kepala Cabang mengurus
              timnya), jadi keduanya duduk bersama alih-alih terserak di
              kantong "Special Ops".

              ⛔ SEKSI INI TIDAK DIGERBANGI PERAN (21 Sep 2026,
              NAV-ABSENSI-SAYA-TERSEMBUNYI-ADMIN-GUDANG-01): "Absensi Saya"
              milik SETIAP jabatan bersesi — endpoint `/attendance/saya/hari-ini`
              memang tanpa gerbang peran dan rute `/hr/*` terbuka untuk semua
              yang login. Gerbang lama `PAPAN_KERJA_ROLES ∪ JAM_MASUK_NAV_ROLES`
              menyembunyikannya dari Admin Gudang/CS/Finance/Purchasing/desainer
              padahal mereka berhak mengabsenkan dirinya. Butir LAIN di seksi ini
              tetap digerbangi satu per satu di bawah. Dijaga
              `tests/test_nav_absensi_saya_semua_jabatan.py`. */}
          {(
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-sdm-section">
              <KepalaKelompok id="sdm" icon={Contact} judul="SDM" />
              {/* Urutan K143:
                    OPERASIONAL  Absensi Saya (SETIAP orang, SETIAP hari —
                                 karena itu paling atas) -> Jam Kerja
                    LAPORAN      Rekap Absensi -> Rekap Upah Borongan
                    PENGATURAN   Personil (master orang — paling bawah) */}
              {/* ⛔ "Absensi Saya" NOL PERNAH punya butir menu sejak lahir
                  11 Agustus 2026 — satu-satunya cara membukanya adalah
                  mengetik URL-nya. Layar kios memang sengaja di luar menu
                  (perangkat bersama, tanpa login); yang ini tidak.

                  ⚠ Tidak digerbangi peran: setiap orang bersesi berhak
                  mengabsenkan DIRINYA SENDIRI, dan endpoint-nya memang
                  sengaja tanpa `_require_read` dengan alasan yang sama —
                  dan sejak 21 Sep 2026 seksinya pun tidak digerbangi
                  (dulu komentar ini benar, gerbang seksinya tidak). */}
              <NavItem to="/hr/absensi-saya" icon={Contact} testId="nav-absensi-saya">Absensi Saya</NavItem>
              <NavItem to="/hrd/izin" icon={Contact} testId="nav-izin">Izin, Sakit & Cuti</NavItem>
              <NavItem to="/hrd/koreksi-absen" icon={Contact} testId="nav-koreksi-absen">Koreksi Absen</NavItem>
              {PAYROLL_ROLES.includes(roleCode) && (
                <NavItem to="/hrd/payroll" icon={Contact} testId="nav-payroll">Payroll Staf Tetap</NavItem>
              )}
              {/* K134 — kelompok HRD. Owner: "Jam masuk diisi oleh kepala
                  cabang masing masing region, bisa dimasukan dalam kelompok
                  HRD."

                  ⚠ SENGAJA di sini, BUKAN di /hr/* — sub-router itu duduk di
                  LUAR <Layout/> supaya layar kios bisa dibuka tanpa login dan
                  tanpa menu. Layar ini justru butuh menu; menaruhnya di sana
                  membuatnya nol bisa dicapai dari navigasi mana pun. */}
              {JAM_MASUK_NAV_ROLES.includes(roleCode) && (
                <NavItem to="/hrd/jam-masuk" icon={Clock} testId="nav-jam-masuk">Jam Kerja</NavItem>
              )}
              {ABSENSI_REKAP_ROLES.includes(roleCode) && (
                <NavItem to="/hrd/rekap-absensi" icon={ClipboardCheck} testId="nav-rekap-absensi">Rekap Absensi</NavItem>
              )}
              {BORONGAN_BACA_ROLES.includes(roleCode) && (
                <NavItem to="/borongan/rekap-upah" icon={HandCoins} testId="nav-rekap-upah">Rekap Upah Borongan</NavItem>
              )}
              {PAPAN_KERJA_ROLES.includes(roleCode) && (
                <NavItem to="/personil" icon={Contact} testId="nav-personil">Personil</NavItem>
              )}
              {KARYAWAN_ROLES.includes(roleCode) && (
                <NavItem to="/hrd/karyawan" icon={Contact} testId="nav-karyawan">Karyawan</NavItem>
              )}
            </div>
          )}

          {/* Dokumen & Audit — yang dibaca saat MEMERIKSA, bukan saat
                mengerjakan. Arsip PDF dan Audit Log punya pembaca dan waktu
                pakai yang sama, dan keduanya sebelumnya terserak di dua
                tempat berbeda. */}
            {(PDF_ARCHIVE_ROLES.includes(roleCode) || LAPORAN_ROLES.includes(roleCode) || PAPAN_KONTROL_ROLES.includes(roleCode) || ANALITIK_ROLES.includes(roleCode) || KPI_ROLES.includes(roleCode) || isOwner || isKepalaCabang) && (
              <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-dokumen-section">
                <KepalaKelompok id="dokumen" icon={Archive} judul="Dokumen & Audit" />
                {/* Urutan K143 — seluruh kelompok ini tier LAPORAN; diurut
                    dari yang paling sering dibaca: Laporan -> HPP & Margin ->
                    Arsip PDF -> Jejak Audit.

                    ⛔ HPP & Margin PINDAH KE SINI dari Pengaturan. Ia LAPORAN
                    (dibaca, nol ada yang disetel di dalamnya), dan di
                    Pengaturan ia satu-satunya butir yang bukan setelan.
                    Gerbangnya ikut apa adanya: isOwner — satu-satunya yang
                    boleh melihat HPP/margin lintas region (CLAUDE.md). */}
                {LAPORAN_ROLES.includes(roleCode) && (
                  <NavItem to="/laporan" icon={ScrollText} testId="nav-laporan">Laporan</NavItem>
                )}
                {ANALITIK_ROLES.includes(roleCode) && (
                  <NavItem to="/laporan/analitik" icon={ScrollText} testId="nav-analitik">Analitik</NavItem>
                )}
                {KPI_ROLES.includes(roleCode) && (
                  <NavItem to="/laporan/kpi" icon={ScrollText} testId="nav-kpi">KPI Ketepatan Waktu</NavItem>
                )}
                {isOwner && (
                  <NavItem to="/hpp" icon={ScrollText} testId="nav-hpp">HPP &amp; Margin</NavItem>
                )}
                {/* K212 (BUSINESS-80/83): biaya potongan DOKU dari laporan settlement + uang kembali pemasok.
                    Finance mencatat, Owner membaca. */}
                {UANG_MASUK_ROLES.includes(roleCode) && (
                  <NavItem to="/keuangan/uang-masuk" icon={HandCoins} testId="nav-uang-masuk">Uang Masuk</NavItem>
                )}
                {/* K180 Papan Kontrol — penyimpangan prosedur & data (bukan
                    keterlambatan: itu alarm produksi; bukan pajangan: itu Papan
                    TV). Tier LAPORAN: dibaca lalu ditindak; ambangnya (Q32)
                    setelan Owner, KC hanya membaca. */}
                {PAPAN_KONTROL_ROLES.includes(roleCode) && (
                  <NavItem to="/papan-kontrol" icon={Siren} testId="nav-papan-kontrol">Papan Kontrol</NavItem>
                )}
                {PAPAN_KONTROL_AMBANG_ROLES.includes(roleCode) && (
                  <NavItem to="/papan-kontrol/ambang" icon={SlidersHorizontal} testId="nav-papan-kontrol-ambang">Ambang Papan Kontrol</NavItem>
                )}
                {PDF_ARCHIVE_ROLES.includes(roleCode) && (
                  <NavItem to="/arsip-pdf" icon={Archive} testId="nav-arsip-pdf">Arsip PDF</NavItem>
                )}
                {(isOwner || isKepalaCabang) && (
                  <NavItem to="/audit-logs" icon={ScrollText} testId="nav-audit">Jejak Audit</NavItem>
                )}
                {isOwner && (
                  <NavItem to="/audit-logs/tinjauan-delegasi" icon={ScrollText} testId="nav-tinjauan-delegasi">Tinjauan Delegasi</NavItem>
                )}
            </div>
          )}
          {(isOwner || isKepalaCabang) && (
            /* ⛔ <details> TERTUTUP, bukan daftar terbuka.
                Sebelas butir Master Data dipakai BULANAN, tapi menempati
                ruang yang sama dengan tujuh butir yang dipakai tiap jam.
                Owner melihat 39 butir nav tanpa pencarian; menutup blok ini
                menurunkannya ke 29 tanpa satu pun akses dicabut.

                ⚠ SENGAJA `<details>` bawaan HTML, bukan komponen buatan:
                ia bisa dibuka dengan papan tik apa adanya, keadaan buka/tutup
                diumumkan pembaca layar tanpa satu pun atribut ARIA, dan
                Ctrl+F browser tetap menemukan isinya. Accordion buatan
                sendiri harus mengerjakan ketiganya lagi, dan biasanya
                mengerjakan yang ketiga dengan salah.

                Inventori dan Purchasing TETAP terbuka — itu pekerjaan
                harian. */
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-master-section">
              <KepalaKelompok id="master" icon={Database} judul="Master Data" />
              {/* Urutan K143 — sebelas butir dikelompokkan menurut APA yang
                  disetelnya, bukan kapan ia dibuat:
                    HARGA     Kelompok Harga -> Daftar Harga -> Biaya Tambahan
                              Ukuran -> Bonus Promo
                    PRODUK    Collar / Kerah -> Setelan Potong Kain
                    PRODUKSI  Paket Waktu Pengerjaan -> Kapasitas per Tahap ->
                              Tarif Borongan
                    UMUM      Hari Libur -> Pengaturan Notifikasi */}
              <NavItem to="/master/price-groups" icon={Database} testId="nav-master-pg">Kelompok Harga</NavItem>
              <NavItem to="/master/price-list-items" icon={Tag} testId="nav-master-pli">Daftar Harga</NavItem>
              {isOwner && <NavItem to="/master/kategori-harga" icon={Tag} testId="nav-master-kategori-harga">Kategori Harga</NavItem>}
              <NavItem to="/master/size-surcharge" icon={Ruler} testId="nav-master-size">Biaya Tambahan Ukuran</NavItem>
              <NavItem to="/master/bonus-promo" icon={Gift} testId="nav-master-promo">Bonus Promo</NavItem>
              <NavItem to="/master/collar" icon={Shirt} testId="nav-master-collar">Collar / Kerah</NavItem>
              <NavItem to="/master/potong-kain" icon={Scissors} testId="nav-master-cutting">Setelan Potong Kain</NavItem>
              <NavItem to="/master/time-package" icon={Clock} testId="nav-master-tp">Paket Waktu Pengerjaan</NavItem>
              <NavItem to="/master/kapasitas-tahap" icon={Gauge} testId="nav-master-kapasitas">Kapasitas per Tahap</NavItem>
              <NavItem to="/master/kapasitas-kenyataan" icon={Gauge} testId="nav-master-kapasitas-kenyataan">Kapasitas vs Kenyataan</NavItem>
              <NavItem to="/master/tarif-borongan" icon={HandCoins} testId="nav-master-sewing-rate">Tarif Borongan</NavItem>
              <NavItem to="/holidays" icon={CalendarDays} testId="nav-holidays">Hari Libur</NavItem>
              {isOwner && <NavItem to="/master/kategori-komplain" icon={MessageSquareWarning} testId="nav-master-kategori-komplain">Kategori Komplain</NavItem>}
              {isOwner && <NavItem to="/master/kanal-wa" icon={BellRing} testId="nav-master-kanal-wa">Alat AI WhatsApp</NavItem>}
              <NavItem to="/master/rekening-resmi" icon={Landmark} testId="nav-master-rekening-resmi">Rekening Resmi Cabang</NavItem>
              <NavItem to="/master/notification-channels" icon={BellRing} testId="nav-master-notif-channels">Pengaturan Notifikasi</NavItem>
            </div>
          )}
          {/* K68 + K72 — FINANCE tidak ikut section Master Data di atas (K65
              tetap OWNER/KEPALA_CABANG untuk keenam layar lain di dalamnya),
              tapi ia BOLEH membaca Tarif Borongan secara spesifik. Section
              terpisah, bukan melonggarkan section di atas — melonggarkannya
              akan menampilkan menu yang route-nya tetap menolak FINANCE. */}
          {roleCode === "FINANCE" && (
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-finance-readonly-section">
              <KepalaKelompok id="finance-readonly" icon={Database} judul="Master Data (Baca)" />
              <NavItem to="/master/tarif-borongan" icon={HandCoins} testId="nav-master-sewing-rate-finance">Tarif Borongan</NavItem>
            </div>
          )}
          {/* Pengaturan — PALING BAWAH, dan itu bukan sisa melainkan tempat
              yang benar: dipakai paling jarang, dan orang mencarinya di ujung.

              ⚠ Audit Log PINDAH ke Dokumen & Audit di atas — ia dibaca saat
              MEMERIKSA, bukan saat mengubah setelan. Gerbangnya ikut apa
              adanya: (isOwner || isKepalaCabang).

              ⚠ HPP & Margin juga PINDAH ke Dokumen & Audit (K143, 2 Sep 2026)
              dengan alasan yang sama: ia laporan, bukan setelan. Yang tersisa
              di sini murni konfigurasi sistem. */}
          {/* Panduan SOP per peran (MANDAT-PRAUAT-26SEP) — untuk SEMUA peran; staf hanya melihat bagian yang
              sudah disahkan Owner. */}
          <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-panduan-section">
            <NavItem to="/panduan" icon={BookOpen} testId="nav-panduan">Panduan</NavItem>
          </div>
          {isOwner && (
            <div className="mt-3 pt-3 border-t border-brand-navy-foreground/10" data-testid="nav-pengaturan-section">
              <KepalaKelompok id="pengaturan" icon={Settings} judul="Pengaturan" />
              <NavItem to="/users" icon={UsersIcon} testId="nav-users">User Management</NavItem>
              <NavItem to="/settings" icon={Settings} testId="nav-settings">Pengaturan</NavItem>
            </div>
          )}
        </nav>
      </SisiNav>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-card border-b px-6 py-3 flex items-center justify-between flex-wrap gap-2">
          {/* ⚠ Nama DAN email hanya ditampilkan berdampingan kalau namanya
              memang ADA. Versi sebelumnya menulis `{full_name || email}` lalu
              titik lalu `{email}` — jadi begitu `full_name` kosong, email yang
              sama muncul DUA KALI dipisah titik. Ditemukan lewat UAT
              27 Agustus 2026; nol test yang bisa melihatnya, karena keduanya
              memang benar secara data. */}
          <div className="text-sm text-muted-foreground">
            {profile?.full_name ? (
              <>
                <span data-testid="header-name" className="font-medium text-foreground">
                  {profile.full_name}
                </span>
                <span className="mx-2">·</span>
                <span data-testid="header-email">{profile.email}</span>
              </>
            ) : (
              <span data-testid="header-email" className="font-medium text-foreground">
                {profile?.email || "—"}
              </span>
            )}
          </div>
          {/* ⛔ Lonceng notifikasi DICABUT 27 Agustus 2026 — keputusan Owner:
              "notifikasi tampil dalam bentuk card di dashboard, saya rasa
              lonceng tidak perlu". Daftar tugas yang penting tidak boleh
              bersembunyi di balik ikon kecil yang harus diklik dulu; ia
              sekarang terbuka di Dashboard. */}
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {/* K200 butir 9: "Tanya ETAMS" di semua layar — asisten AI staf tingkat 1–3 (hanya membaca). */}
            <TanyaEtams />
            {/* "?" — panduan untuk layar INI (bagian panduan yang menyebut rute ini). */}
            <Button variant="ghost" size="icon" title="Panduan untuk layar ini" aria-label="Panduan untuk layar ini"
                    data-testid="btn-panduan-layar"
                    onClick={() => navigate(`/panduan?layar=${encodeURIComponent(pathname)}`)}>
              <CircleHelp size={18} />
            </Button>
            <Badge data-testid="header-role" variant="outline">{profile?.role?.name || "—"}</Badge>
            <Badge data-testid="header-app-mode" data-mode={mode}
                   title={LABEL_MODE[mode]?.title || ""}
                   className={mode === "DIBEKUKAN" ? "bg-danger text-danger-foreground" : mode === "PRODUCTION" ? "bg-primary text-primary-foreground" : "bg-info-soft text-info-soft-foreground"}>
              {LABEL_MODE[mode]?.label || mode}
            </Badge>
            {/* Bertindak Atas Nama (K140) — permanen */}
            <DevRoleSwitcher />
            {/* Pilihan tema manual — permintaan Owner 30 Agustus 2026.
                Mekanismenya sudah lengkap sejak #4b-1 (ThemeContext, token
                `.dark` di index.css, `darkMode: ["class"]` di Tailwind); yang
                belum ada cuma kendalinya.

                ⛔ KOREKSI 31 Agustus 2026 — "11 dari 43 halaman masih memakai
                warna mati" SUDAH TIDAK BENAR. Diukur ulang hari itu dengan
                menyapu seluruh `pages/` + `components/`: yang tersisa TIGA,
                dan ketiganya sudah ditambal (tombol `GroupJahit`, penanda
                langkah `WOCreate`, lima badge arah `Transfers`).

                DUA berkas sengaja DIBIARKAN, dan keduanya benar apa adanya:
                  • `RuangPesanan.jsx` — layar CUSTOMER, halaman berdiri
                    sendiri yang mengecat groundnya sendiri (`min-h-screen
                    latar abu-abu terang`). Menokenkannya berarti HP customer ikut
                    mengikuti toggle gelap STAF — kendali yang bukan miliknya.
                  • `EvidenceManager.jsx` — latar gelap di balik gambar
                    lightbox. Itu benar di KEDUA tema, bukan warna mati.

                Owner memintanya tetap dipasang; yang dipilih di sini adalah
                bentuk yang PALING BISA DIBATALKAN — satu ketukan kembali ke
                terang, per perangkat (Q63), nol menyentuh database. */}
            <Button
              data-testid="theme-toggle"
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={toggleTheme}
              title={theme === "dark" ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
              aria-label={theme === "dark" ? "Ganti ke mode terang" : "Ganti ke mode gelap"}
            >
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </Button>
            {/* ⛔ LOGOUT PINDAH KE SINI dari dasar sidebar, 27 Agustus 2026.
                Ia BUKAN navigasi, dan di sidebar ia terletak di bawah butir
                ke-39 — di balik gulir. Agent sendiri salah klik ke "Pembayaran
                Vendor" saat mencarinya waktu UAT; kalau yang menulis kodenya
                pun meleset, staf yang buru-buru pasti meleset.

                Di header ia selalu terlihat tanpa menggulir, dan duduk di
                tempat yang memang dicari orang: dekat identitas dirinya. */}
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={handleSignOut}
            >
              <LogOut size={16} className="mr-1.5" /> Keluar
            </Button>
          </div>
        </header>
        {/* Pita BERTINDAK ATAS NAMA (K140).

            ⛔ KOMENTAR LAMA DI SINI MENULIS "DEV ONLY — pita penyamaran".
               Dicabut K140: ini fitur permanen, dan katanya bukan
               "penyamaran" — yang dipinjam WEWENANG, bukan IDENTITAS.

            Badge saja terlalu mudah terlewat; yang dicegah di sini adalah
            momen orang lupa ia sedang bertindak atas nama peran lain lalu
            bingung kenapa tombolnya berbeda.

            ⚠ Kalimat "Role aslimu tetap Owner" DIHAPUS — sejak K140,
              Kepala Cabang juga bisa mendelegasi, dan kalimat yang menyebut
              Owner akan berbohong ke separuh pemakainya.

            ⚠ NADANYA dilunakkan 2 Sep 2026. Versi pertama berbunyi "setiap
              tindakan tetap tercatat atas namamu sendiri" — faktanya benar,
              tapi membacanya seperti teguran. Owner menyebut rasa itu:
              "seakan-akan sistem tidak percaya". Fakta yang sama disampaikan
              sebagai keterangan, bukan peringatan. */}
        {/* K236: akun Pemantau — hanya baca. Pita ini menjelaskan kenapa tombol simpan/setujui tidak ada. */}
        {isPemantau && (
          <div data-testid="pita-pemantau" role="status"
               className="bg-info-soft text-info-soft-foreground px-6 py-1.5 text-xs font-medium">
            Akun <strong>Pemantau</strong> — hanya bisa melihat. Tombol yang mengubah data tidak ditampilkan.
          </div>
        )}
        {impersonation && (
          // T102 (Titipan auditor 1 Nov 2026): pita MERAH tetap di semua layar (di luar <main> yang menggulir) —
          // "Sedang atas nama <jabatan> — pelaku asli <nama · jabatan>". Setiap aksi tercatat pelaku asli +
          // `atas_nama_role` di audit (delegasi_ctx), jadi kalimatnya tetap keterangan, bukan teguran.
          <div
            data-testid="impersonation-strip"
            role="status"
            className="bg-danger text-danger-foreground px-6 py-1.5 text-xs font-medium"
          >
            Sedang atas nama <strong>{impersonation.as_role_name}</strong>
            {impersonation.as_region_name ? ` · ${impersonation.as_region_name}` : ""}
            {impersonation.as_store_name ? ` · ${impersonation.as_store_name}` : ""}
            {" — "}Anda bertindak atas nama jabatan ini; pelaku yang tercatat tetap{" "}
            <strong>{profile?.full_name || impersonation.real_email || "Anda"}</strong>
            {impersonation.real_role_name ? ` · ${impersonation.real_role_name}` : ""}.
          </div>
        )}
        {/* ⛔ Berpindah halaman WAJIB membuka dari paling atas — permintaan
            Owner 31 Agustus 2026: *"agar tidak ada informasi yg terlewatkan
            dan atau agar tidak tampak kosong / belum berjalan"*.

            ⚠ `window.scrollTo` NOL BERPENGARUH di sini. Yang menggulir adalah
            `<main>` (`overflow-auto`), bukan jendela — jendelanya sendiri nol
            pernah punya gulir. Memasang penggulir di jendela akan terlihat
            benar di kode dan nol mengubah apa pun di layar, dan cacat seperti
            itu bertahan lama justru karena "sudah ada kodenya". */}
        <main
          ref={mainRef}
          className="flex-1 p-6 overflow-auto"
          data-testid="main-content"
        >
          {/* Remah roti (Tahap 1 kematangan ERP, 2 Sep 2026) — di DALAM
              <main>, bukan di header: ia bagian dari halaman yang dibaca,
              dan ikut kembali ke atas saat rute berganti. Provider-nya
              membungkus Outlet supaya halaman detail bisa menyetel butir
              terakhir (nomor FA/WO) lewat useJudulHalaman(). */}
          {mode === "DIBEKUKAN" && (
            // BUSINESS-42: semua staf harus tahu kenapa simpan ditolak (server menjawab 423 SISTEM_DIBEKUKAN).
            <div role="alert" data-testid="banner-dibekukan"
                 className="mb-4 rounded border border-danger-soft-border bg-danger-soft p-3 text-sm text-danger-soft-foreground">
              Sistem sedang <b>DIBEKUKAN</b> oleh Owner. Data tetap bisa dilihat dan dicetak, tetapi input baru
              berhenti sampai Owner mencairkannya.
            </div>
          )}
          <JudulHalamanProvider>
            <Breadcrumb />
            <Outlet />
          </JudulHalamanProvider>
        </main>
        <TombolKeAtas wadahRef={mainRef} />
      </div>
      <CariMenu />
    </div>
    </NavProvider>
    </HanyaBacaContext.Provider>
  );
}

/* Bingkai sidebar. Versi A (lebar, bawaan) atau Versi B (ciut: rail ikon kelompok) — pilihan
   per user (useNav().ciut). Di Versi B nav penuh TETAP terpasang tapi disembunyikan: butir-butirnya
   yang mendaftarkan diri (lolos gerbang peran) menjadi sumber rail, jadi rail tidak pernah
   memuat menu yang tidak boleh dilihat. Layar HP selalu Versi A (tombol ciut hanya md ke atas). */
function SisiNav({ children }) {
  const nav = useNav();
  const ciut = !!(nav && nav.ciut);
  return (
    <aside className={`w-full min-w-0 max-w-full overflow-x-auto md:overflow-x-visible ${ciut ? "md:w-16" : "md:w-60"} bg-brand-navy text-brand-navy-foreground flex md:flex-col md:sticky md:top-0 md:h-screen`}
           data-testid="sidebar" data-ciut={ciut ? "1" : "0"}>
      <div className={`${ciut ? "md:px-2 md:py-4" : "p-4"} p-4 border-b border-brand-navy-foreground/10 flex-1 md:flex-none flex items-start justify-between gap-2`}>
        <div className={ciut ? "md:hidden" : ""}>
          <div className="text-lg font-bold tracking-tight">ETAMS</div>
          <div className="text-[11px] text-brand-navy-foreground/60 mt-0.5">Internal Operating System</div>
        </div>
        <TombolCiut />
      </div>
      {ciut && <div className="hidden md:block flex-1 overflow-visible"><RailCiut /></div>}
      <div className={`${ciut ? "md:hidden" : ""} flex md:flex-col flex-1 min-h-0 min-w-0`}>{children}</div>
    </aside>
  );
}
